import { useRef, useState, createContext, useEffect, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, LinearProgress, Snackbar, Typography } from '@mui/material'
import Drag from './drag.svg?react'
import { useBeforeUnload } from 'react-router'
import ImageUploader from './ImageUploader'
import ModalResult from './ModalResult'
import { readAndCompressImage, type Config } from 'browser-image-resizer'
import {HourglassEmptySharp} from '@mui/icons-material'
import type { UploadStates, UploadResults } from '../../utils/little-parts'

const readEntries = (entry: FileSystemEntry): Promise<File[]> =>
  entry.isFile ?
    new Promise<File[]>(resolve=> {
      (entry as FileSystemFileEntry).file((file) => resolve([file]))
    })
  : entry.isDirectory ?
    new Promise<File[]>(resolve=> {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      reader.readEntries(async (entries) => {
        const nestedFiles = await Promise.all(entries.map(readEntries))
        resolve(nestedFiles.flat())
      })
    })
  :
    Promise.resolve([])

const resize_config = {
  quality: 0.9,
  maxWidth: 240,
  maxHeight: 240,
  mimeType: 'image/jpeg',
} as const satisfies Config

export const ImageUploaderContext = createContext<{
  uploader: RefObject<ImageUploader>
  is_uploading: boolean
  upload_success: undefined[]
}>(undefined as never)

export const ImageUploaderProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [upload_states, setUploadStates] = useState<UploadStates>({files: [], results: []})
  const [results, setResults] = useState<UploadResults>([])
  const image_uploader = useRef(new ImageUploader(setUploadStates, setResults))
  const [on_drag, setOnDrag] = useState(false)
  const {t} = useTranslation()
  const [current_image_src, setCurrentImageSrc] = useState<string>()
  const is_uploading = upload_states.files.length !== upload_states.results.length
  const upload_errors = upload_states.results.filter(r=> r !== undefined)
  const upload_success = upload_states.results.filter(r=> r === undefined)
  const upload_current = upload_states.files[upload_states.results.length] as File | undefined

  useBeforeUnload(e=> {
    if (is_uploading) {
      e.preventDefault()
      // 非推奨だし発火しないかもしれないが一応
      // 空文字なら許されるっぽいが
      e.returnValue = ''
    }
  })

  useEffect(()=> {
    if (upload_current) {
      let src: string | undefined
      readAndCompressImage(upload_current, resize_config).then(resized=> {
        src = URL.createObjectURL(resized)
        setCurrentImageSrc(src)
      })
      return ()=> {
        if (src) URL.revokeObjectURL(src)
      }
    }
  }, [upload_current])

  return <>
    <ImageUploaderContext value={{uploader: image_uploader, is_uploading, upload_success}}>
      <div onDragEnter={e=> {
        e.preventDefault()
        if (document.body.style.overflow === 'hidden' || document.querySelector('[role="dialog"]')) return
        // D&Dはファイルのみ
        // 画像とかがfile扱いされたりするので
        const is_in_file = [...e.dataTransfer.items].every(item=> item.kind === 'file')
        if (is_in_file) setOnDrag(true)
      }}>
        {children}
      </div>
    </ImageUploaderContext>

    {on_drag && <Box
      sx={(theme) => ({
        position: 'fixed',
        top: 0, left: 0,
        width: '100%', height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        ...theme.applyStyles('dark', {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        }),
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        flexDirection: 'column',
        zIndex: 9
      })}
    >
      <div style={{textAlign: 'center'}}>
        <Drag style={{display: 'block'}}/>
        <br/>
        <Typography fontWeight='bold' fontSize='32px'>
          {t('upload.drag_over')}
        </Typography>
      </div>
      <div
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: '100%',
          zIndex: 10
        }}
        onDragLeave={()=> { setOnDrag(false) }}
        onDragEnd={()=> { setOnDrag(false) }}
        onDragOver={e=> { e.preventDefault() }}
        onDrop={async e=> {
          e.preventDefault()
          const {items} = e.dataTransfer
          const filess = await Promise.all([...items].map(item=> readEntries(item.webkitGetAsEntry()!)))
          image_uploader.current.append(filess.flat())
          setOnDrag(false)
        }}
      />
    </Box>}

    <Snackbar
      anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
      open={is_uploading}
      autoHideDuration={5000}
      sx={{
        '& .MuiSnackbarContent-message': {
          width: '100%',
        }
      }}
      message={<>
        <div style={{display: 'flex', height: '100%', gap: '4px'}}>
          <div style={{flexGrow: 1, display: 'flex', flexFlow: 'column'}}>
            <div style={{flexGrow: 1}}>
              <div style={{fontWeight: 'bold', fontSize: 16, marginBottom: '4px'}}>
                {upload_success.length}
                {' '}
                {upload_errors.length > 0 && <span style={{color: 'red'}}>
                  ({t('general.error')}: {upload_errors.length}){' '}
                </span>}
                / {upload_states.files.length}
              </div>
              {t('upload.uploading_photos')}
            </div>
            <div>
              <Button
                type='button' color='warning' size='medium'
                sx={{padding: '2px 0'}}
                onClick={()=> { image_uploader.current.cancel() }}
              >
                {t('general.cancel')}
              </Button>
            </div>
          </div>
          <div>
            {current_image_src ?
              <img
                src={current_image_src}
                alt='uploading photo'
                height={100} width={100}
                style={{objectFit: 'cover', borderRadius: 6}}
              />
            :
              <Box sx={{
                width: 100, height: 100, borderRadius: 6, bgcolor: 'gray_b.light',
                textAlign: 'center', alignContent: 'center'
              }}>
                <HourglassEmptySharp fontSize='large' sx={{color: 'text.secondary'}}/>
              </Box>
            }
          </div>
        </div>
        <Box sx={{width: '100%', marginTop: '6px'}}>
          <LinearProgress
            color='primary' variant='determinate'
            sx={{
              height: 12, borderRadius: 6, backgroundColor: '#d2d2d2',
              '& .MuiLinearProgress-bar': { borderRadius: 6 },
            }}
            value={
              (current_image_src ? 33.3 : 0) / upload_states.files.length
              + 100 * upload_states.results.length / upload_states.files.length
            }
          />
        </Box>
      </>}
    />

    <ModalResult results={results} setResults={setResults}/>
  </>
}